/**
 * GridCellsPanel - Panel de celdas de malla en el RightPanel
 *
 * @version 1.0
 * @updated 2026-02-25
 * @module core/layouts/panels/RightPanel
 *
 * @description
 * Muestra las celdas de la malla activa en el panel derecho.
 * Utiliza el componente CellsSection para renderizar la tabla de celdas.
 *
 * @remarks
 * - Se abre cuando el usuario activa una malla con el botón Power
 * - Proporciona más espacio para trabajar con las celdas
 * - Libera espacio en el panel izquierdo para la lista de mallas
 */

import { X } from 'lucide-react';
import { CellsSection } from '../LeftPanel/CellsSection';
import type { SpatialGrid, CellRow } from '../../../spatial';
import './grid-cells-panel.css';

/**
 * Props del componente GridCellsPanel
 */
export interface GridCellsPanelProps {
  /**
   * Malla activa
   */
  activeGrid: SpatialGrid;

  /**
   * Filas de celdas a mostrar
   */
  cellRows: CellRow[];

  /**
   * H3 index de la celda actualmente hover
   */
  hoveredCellId: string | null;

  /**
   * Callback cuando el usuario hace hover sobre una fila
   */
  onCellHover: (h3Index: string | null) => void;

  /**
   * H3 index de la celda seleccionada
   */
  selectedCellId: string | null;

  /**
   * Callback cuando el usuario selecciona/deselecciona una celda
   */
  onCellSelect: (h3Index: string | null) => void;

  /**
   * Vuela al centro de la celda en el mapa
   */
  onZoomToCell: (h3Index: string) => void;

  /**
   * Guarda el nombre personalizado de una celda
   */
  onCellNameChange: (h3Index: string, name: string) => Promise<void>;

  /**
   * Callback cuando se hace clic en el ícono de estadísticas
   */
  onCellStatsClick: (h3Index: string) => void;

  /**
   * Callback cuando el usuario cierra el panel
   */
  onClose: () => void;
}

/**
 * Panel de celdas de malla en el RightPanel.
 *
 * @remarks
 * Proporciona un espacio amplio para visualizar y editar las celdas de la malla activa.
 */
export function GridCellsPanel({
  activeGrid,
  cellRows,
  hoveredCellId,
  onCellHover,
  selectedCellId,
  onCellSelect,
  onZoomToCell,
  onCellNameChange,
  onCellStatsClick,
  onClose,
}: GridCellsPanelProps): JSX.Element {
  return (
    <div className="grid-cells-panel">
      {/* Header del panel */}
      <header className="grid-cells-panel-header">
        <div className="grid-cells-panel-title-group">
          <h2 className="grid-cells-panel-title">Celdas</h2>
          <span className="grid-cells-panel-grid-name">{activeGrid.name}</span>
        </div>
        <button
          className="grid-cells-panel-close-btn"
          onClick={onClose}
          title="Cerrar panel"
          aria-label="Cerrar panel de celdas"
        >
          <X size={16} />
        </button>
      </header>

      {/* Contenido: CellsSection sin el header (ya lo tenemos arriba) */}
      <div className="grid-cells-panel-content">
        <CellsSection
          activeGrid={activeGrid}
          cellRows={cellRows}
          hoveredCellId={hoveredCellId}
          onCellHover={onCellHover}
          selectedCellId={selectedCellId}
          onCellSelect={onCellSelect}
          onZoomToCell={onZoomToCell}
          onCellNameChange={onCellNameChange}
          onCellStatsClick={onCellStatsClick}
        />
      </div>
    </div>
  );
}
